<script setup>
/**
 * FormControlContentClass
 * Handles deprecated class name fields. Fetches existing class usage from
 * the API to populate a datalist, or hides the field if no data exists.
 */
import { computed, onMounted, getCurrentInstance } from 'vue';
import FormControlTypeText from '../form-control-type/FormControlTypeText.vue';

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number],
        default: ''
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- Computed ---

const tableName = computed(() => store.state[props.formId]?.tableName);

// --- Methods ---

/**
 * Fetches unique class names used in the current table/field context.
 * Populates field.datalist and toggles visibility based on results.
 */
const setFieldDatalist = async () => {
    if (!tableName.value) return;

    try {
        const response = await store.dispatch('request/get', {
            url: `api/component/${tableName.value}/${props.field.name}`,
            params: { sort: props.field.name }
        });

        const valuesSet = new Set();

        // Flatten space-separated classes into unique values
        (response.data.values || []).forEach((str) => {
            if (!str) return;
            str.split(' ').forEach(className => {
                if (className.trim()) {
                    valuesSet.add(className.trim());
                }
            });
        });

        const sortedValues = Array.from(valuesSet).sort();

        // If there are no values, remove the field completely, else show it
        if (sortedValues.length > 0) {
            props.field.visibility = '';
            props.field.datalist = sortedValues;
        } else {
            props.field.hidden = true;
        }

    } catch (error) {
        console.error('Failed to fetch class name datalist:', error);
        props.field.hidden = true;
    }
};

// --- Lifecycle ---

onMounted(() => {
    /**
     * The Class names field is deprecated and should only be available
     * if already used by the site. We hide it by default until data is confirmed.
     */
    props.field.visibility = 'hidden';

    setFieldDatalist();
});
</script>

<template>
    <FormControlTypeText
        v-bind="$props"
        v-on="$listeners"
    />
</template>